import React, { useState, useRef, useEffect } from 'react';
import { Check, ChevronDown, ChevronUp, Eye, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { NewLeadIcon, WaitIcon, SendEmailIcon, ConditionIcon, DeleteIcon } from './CustomIcons';
import { buildEmailPreviewHtml, buildPreviewFromTemplate } from '../email-preview.helper';
import { EmailTemplate } from '../../timeline/components/timeline-types';
import { EmailTemplatesService } from '../../timeline/services/email-templates.service';

export type NodeType = 'trigger' | 'condition' | 'delay' | 'action';

export interface NodeData {
  id: string;
  type: NodeType;
  title: string;
  subtitle?: string;
  config?: any;
}

interface DraggableNodeProps {
  node: NodeData & { x: number; y: number };
  onDrag: (nodeId: string, deltaX: number, deltaY: number) => void;
  onDragEnd?: (nodeId: string) => void;
  onClick: (nodeId: string) => void;
  onDelete: (nodeId: string) => void;
  onConfigChange?: (nodeId: string, config: any) => void;
  onPortDragStart?: (nodeId: string, portType: 'output') => void;
  onPortDrop?: (nodeId: string, portType: 'input') => void;
  isConnecting?: boolean;
  hasError?: boolean;
}

const getNodeIcon = (type: NodeType) => {
  switch (type) {
    case 'trigger':
      return NewLeadIcon;
    case 'condition':
      return ConditionIcon;
    case 'delay':
      return WaitIcon;
    case 'action':
      return SendEmailIcon;
    default:
      return NewLeadIcon;
  }
};

const getDefaultConfig = (type: NodeType) => {
  switch (type) {
    case 'trigger':
      return {
        triggerType: 'new_lead',
        description: 'Triggers when a new lead is detected'
      };
    case 'delay':
      return {
        delay: 2,
        delayUnit: 'hours'
      };
    case 'condition':
      return {
        filterType: 'none',
        countries: '',
        familySlug: ''
      };
    case 'action':
      return {
        autoSelectProducts: true,
        maxProducts: 4,
        emailTemplate: 'modern-template',
        productLayout: 'minimal-list',
        useAI: true
      };
    default:
      return {};
  }
};

/** A labelled field inside the node's expanded panel — every config uses it. */
const ConfigField = ({ label, children, className }: { label: string; children: React.ReactNode; className?: string }) => (
  <div className={className}>
    <Label className="mb-1 block text-xs text-muted-foreground">{label}</Label>
    {children}
  </div>
);

export const DraggableNode: React.FC<DraggableNodeProps> = ({
  node,
  onDrag,
  onDragEnd,
  onClick,
  onDelete,
  onConfigChange,
  onPortDragStart,
  onPortDrop,
  isConnecting = false,
  hasError = false,
}) => {
  const { t } = useTranslation();
  const [isDragging, setIsDragging] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [localConfig, setLocalConfig] = useState<any>(node.config || getDefaultConfig(node.type));
  const [showPreview, setShowPreview] = useState(false);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);

  // Load templates for action node + auto-set templateHtml if missing
  useEffect(() => {
    if (node.type === 'action') {
      const all = EmailTemplatesService.getAllTemplates();
      setTemplates(all);
      // If template is selected but templateHtml is missing, populate it
      const currentId = localConfig.emailTemplate;
      if (currentId && !localConfig.templateHtml) {
        const found = all.find(t => t.id === currentId);
        if (found && onConfigChange) {
          onConfigChange(node.id, { ...localConfig, templateHtml: found.htmlContent });
        }
      }
    }
  }, [node.type]);

  const dragRef = useRef({ startX: 0, startY: 0, hasMoved: false, active: false });
  const IconComponent = getNodeIcon(node.type);

  useEffect(() => {
    setLocalConfig(node.config || getDefaultConfig(node.type));
  }, [node.config, node.type]);

  // Global mouse handlers — attached once, use ref to track state
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!dragRef.current.active) return;
      const deltaX = e.clientX - dragRef.current.startX;
      const deltaY = e.clientY - dragRef.current.startY;
      if (!dragRef.current.hasMoved && (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3)) {
        dragRef.current.hasMoved = true;
        setIsDragging(true);
      }
      if (dragRef.current.hasMoved) {
        onDrag(node.id, deltaX, deltaY);
        dragRef.current.startX = e.clientX;
        dragRef.current.startY = e.clientY;
      }
    };

    const handleMouseUp = () => {
      if (!dragRef.current.active) return;
      const wasMoved = dragRef.current.hasMoved;
      dragRef.current.active = false;
      dragRef.current.hasMoved = false;
      setIsDragging(false);
      if (wasMoved && onDragEnd) onDragEnd(node.id);
    };

    // Attach to window to catch mouseup even outside the browser
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('blur', handleMouseUp); // Release on window blur

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('blur', handleMouseUp);
    };
  }, [node.id, onDrag, onDragEnd]);

  const handleMouseDown = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    // Let form controls own their events; a mousedown that starts a drag would
    // eat the click. Radix selects are buttons with role="combobox".
    if (target.closest('button') || target.closest('input') || target.closest('textarea') ||
        target.closest('select') || target.closest('[role="combobox"]') || target.closest('label')) {
      return;
    }
    e.preventDefault();
    dragRef.current = { startX: e.clientX, startY: e.clientY, hasMoved: false, active: true };
  };

  const handleToggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  const handleSaveConfig = () => {
    // Config is already saved via live updates, just close the expansion
    setIsExpanded(false);
  };

  const handleConfigFieldChange = (field: string, value: any) => {
    const newConfig = { ...localConfig, [field]: value };
    setLocalConfig(newConfig);
    // Update immediately for live preview
    if (onConfigChange) {
      onConfigChange(node.id, newConfig);
    }
  };

  const getSubtitleFromConfig = () => {
    if (!localConfig) return node.subtitle;

    switch (node.type) {
      case 'trigger':
        if (localConfig.triggerType === 'inactive_lead') {
          return t('AUTOMATION.TRIGGER_DESC.INACTIVE', { days: localConfig.inactiveDays || 7 });
        }
        if (localConfig.triggerType === 'product_views') {
          return t('AUTOMATION.TRIGGER_DESC.PRODUCT_VIEWS', { count: localConfig.minViews || 3, days: localConfig.withinDays || 7 });
        }
        return t('AUTOMATION.TRIGGER_DESC.NEW_LEAD');
      case 'condition':
        if (localConfig.filterType === 'country') return `${t('AUTOMATION.CONDITION_TYPES.COUNTRY')}: ${localConfig.countries || '...'}`;
        return t('AUTOMATION.CONDITION_TYPES.NONE');
      case 'delay':
        const unit = localConfig.delayUnit || 'hours';
        const unitKey = unit.toUpperCase();
        return `${t('AUTOMATION.WAIT_BEFORE_SENDING', { value: localConfig.delay || 2, unit: t(`PAGES.EMAIL_AUTOMATION.DELAY_CONFIG.UNITS.${unitKey}`) })}`;
      case 'action':
        const parts = [];
        if (localConfig.subject) parts.push(localConfig.subject);
        else parts.push(localConfig.emailTemplate === 'classic' ? t('PAGES.EMAIL_AUTOMATION.ACTION_CONFIG.TEMPLATES.CLASSIC') : t('PAGES.EMAIL_AUTOMATION.ACTION_CONFIG.TEMPLATES.MODERN'));
        if (localConfig.useAI !== false) parts.push('AI');
        return parts.join(' · ');
      default:
        return node.subtitle;
    }
  };

  const renderTriggerConfig = () => (
    <div className="flex flex-col gap-3 pt-3">
      <ConfigField label={t('PAGES.EMAIL_AUTOMATION.TRIGGER_CONFIG.TRIGGER_TYPE')}>
        <Select
          value={localConfig.triggerType || 'new_lead'}
          onValueChange={(value) => handleConfigFieldChange('triggerType', value)}
        >
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {['new_lead', 'inactive_lead', 'product_views'].map((type) => (
              <SelectItem key={type} value={type}>
                {t(`PAGES.EMAIL_AUTOMATION.TRIGGER_CONFIG.TYPES.${type.toUpperCase()}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </ConfigField>

      {/* Conditional fields based on trigger type */}
      {localConfig.triggerType === 'inactive_lead' && (
        <ConfigField label={t('AUTOMATION.INACTIVE_DAYS')}>
          <Input
            type="number"
            min={1}
            value={localConfig.inactiveDays ?? 7}
            onChange={(e) => handleConfigFieldChange('inactiveDays', Number(e.target.value) || 0)}
          />
        </ConfigField>
      )}

      {localConfig.triggerType === 'product_views' && (
        <div className="flex gap-2">
          <ConfigField className="flex-1" label={t('AUTOMATION.MIN_VIEWS')}>
            <Input
              type="number"
              min={1}
              value={localConfig.minViews ?? 3}
              onChange={(e) => handleConfigFieldChange('minViews', Number(e.target.value) || 0)}
            />
          </ConfigField>
          <ConfigField className="flex-1" label={t('AUTOMATION.WITHIN_DAYS')}>
            <Input
              type="number"
              min={1}
              value={localConfig.withinDays ?? 7}
              onChange={(e) => handleConfigFieldChange('withinDays', Number(e.target.value) || 0)}
            />
          </ConfigField>
        </div>
      )}
    </div>
  );

  const renderConditionConfig = () => (
    <div className="flex flex-col gap-3 pt-3">
      <ConfigField label={t('AUTOMATION.CONDITION_TYPE')}>
        <Select
          value={localConfig.filterType || 'none'}
          onValueChange={(value) => handleConfigFieldChange('filterType', value)}
        >
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {['none', 'country'].map((ft) => (
              <SelectItem key={ft} value={ft}>
                {t(`AUTOMATION.CONDITION_TYPES.${ft.toUpperCase()}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </ConfigField>

      {localConfig.filterType === 'country' && (
        <ConfigField label={t('AUTOMATION.COUNTRIES')}>
          <Input
            placeholder="FR, US, DE"
            value={localConfig.countries || ''}
            onChange={(e) => handleConfigFieldChange('countries', e.target.value)}
          />
        </ConfigField>
      )}
    </div>
  );

  const renderDelayConfig = () => (
    <div className="flex gap-2 pt-3">
      <ConfigField className="flex-1" label={t('PAGES.EMAIL_AUTOMATION.DELAY_CONFIG.DURATION')}>
        <Input
          type="number"
          min={1}
          max={999}
          value={localConfig.delay ?? 2}
          onChange={(e) => {
            const value = e.target.value === '' ? '' : Number(e.target.value);
            handleConfigFieldChange('delay', value);
          }}
        />
      </ConfigField>
      <ConfigField className="flex-1" label={t('PAGES.EMAIL_AUTOMATION.DELAY_CONFIG.UNIT')}>
        <Select
          value={localConfig.delayUnit || 'hours'}
          onValueChange={(value) => handleConfigFieldChange('delayUnit', value)}
        >
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {['minutes', 'hours', 'days', 'weeks'].map((unit) => (
              <SelectItem key={unit} value={unit}>
                {t(`PAGES.EMAIL_AUTOMATION.DELAY_CONFIG.UNITS.${unit.toUpperCase()}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </ConfigField>
    </div>
  );

  const renderActionConfig = () => {
    return (
      <div className="flex flex-col gap-3 pt-3">
        {/* Template */}
        <ConfigField label={t('PAGES.EMAIL_AUTOMATION.ACTION_CONFIG.EMAIL_TEMPLATE')}>
          <Select
            value={localConfig.emailTemplate || undefined}
            onValueChange={(templateId) => {
              const template = templates.find(t => t.id === templateId);
              const newConfig = {
                ...localConfig,
                emailTemplate: templateId,
                ...(template ? { templateHtml: template.htmlContent } : {}),
              };
              setLocalConfig(newConfig);
              if (onConfigChange) onConfigChange(node.id, newConfig);
            }}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {templates.map(tmpl => (
                <SelectItem key={tmpl.id} value={tmpl.id}>{tmpl.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </ConfigField>

        {/* Product Layout */}
        <ConfigField label={t('AUTOMATION.PRODUCT_LAYOUT')}>
          <Select
            value={localConfig.productLayout || 'minimal-list'}
            onValueChange={(value) => handleConfigFieldChange('productLayout', value)}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {['minimal-list', 'grid-cards', 'featured-hero', 'compact-tiles'].map(l => (
                <SelectItem key={l} value={l}>{t(`AUTOMATION.LAYOUTS.${l.replace(/-/g, '_').toUpperCase()}`)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </ConfigField>

        {/* Checkboxes */}
        <div className="flex flex-col gap-1.5">
          <Label className="flex items-center gap-2 text-xs font-normal">
            <Checkbox
              checked={localConfig.autoSelectProducts !== false}
              onCheckedChange={(checked) => handleConfigFieldChange('autoSelectProducts', checked === true)}
            />
            {t('PAGES.EMAIL_AUTOMATION.ACTION_CONFIG.AUTO_SELECT_PRODUCTS')}
          </Label>
          {localConfig.autoSelectProducts !== false && (
            <ConfigField className="ms-6" label={t('PAGES.EMAIL_AUTOMATION.ACTION_CONFIG.MAX_PRODUCTS')}>
              <Input
                type="number"
                min={1}
                max={10}
                value={localConfig.maxProducts ?? 4}
                onChange={(e) => handleConfigFieldChange('maxProducts', e.target.value === '' ? '' : Number(e.target.value))}
              />
            </ConfigField>
          )}
          <Label className="flex items-center gap-2 text-xs font-normal">
            <Checkbox
              checked={localConfig.useAI !== false}
              onCheckedChange={(checked) => handleConfigFieldChange('useAI', checked === true)}
            />
            {t('AUTOMATION.USE_AI')}
          </Label>
        </div>

        {/* Preview / Edit Template button */}
        <Button
          variant="outline"
          size="sm"
          onClick={(e) => { e.stopPropagation(); setShowPreview(true); }}
        >
          <Eye className="size-3.5" />
          {localConfig.useAI === false ? t('AUTOMATION.EDIT_TEMPLATE') : t('AUTOMATION.PREVIEW_EMAIL')}
        </Button>
      </div>
    );
  };

  return (
    <div
      // Node position is canvas data, and the canvas is an LTR pixel space.
      className="absolute transition-[width] duration-300"
      style={{
        left: node.x,
        top: node.y,
        width: isExpanded ? (node.type === 'action' ? 300 : 280) : 220,
        zIndex: isExpanded ? 1001 : (isDragging ? 1000 : (isHovered ? 100 : 1)),
      }}
    >
      <div
        onMouseDown={handleMouseDown}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={cn(
          'relative w-full border bg-background p-3 transition-all select-none',
          isDragging ? 'scale-[1.02] cursor-grabbing shadow-md' : 'cursor-grab shadow-sm',
          hasError
            ? 'border-destructive ring-2 ring-destructive/15'
            : isExpanded
              ? 'border-foreground'
              : isHovered && 'border-foreground/40',
        )}
      >
        {/* Action buttons — top end corner, revealed on hover */}
        <div
          className={cn(
            'absolute end-1.5 top-1.5 flex gap-1 transition-opacity',
            isHovered || isExpanded ? 'opacity-100' : 'opacity-0',
          )}
        >
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon-xs"
                onClick={handleToggleExpand}
                aria-label={isExpanded ? t('PAGES.EMAIL_AUTOMATION.BUTTONS.COLLAPSE') : t('PAGES.EMAIL_AUTOMATION.BUTTONS.EXPAND')}
              >
                {isExpanded ? <ChevronUp className="size-3.5" /> : <ChevronDown className="size-3.5" />}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {isExpanded ? t('PAGES.EMAIL_AUTOMATION.BUTTONS.COLLAPSE') : t('PAGES.EMAIL_AUTOMATION.BUTTONS.EXPAND')}
            </TooltipContent>
          </Tooltip>
          {!isExpanded && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon-xs"
                  className="hover:border-destructive/40 hover:bg-destructive/5 hover:text-destructive"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(node.id);
                  }}
                  aria-label={t('PAGES.EMAIL_AUTOMATION.BUTTONS.DELETE')}
                >
                  <DeleteIcon className="size-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t('PAGES.EMAIL_AUTOMATION.BUTTONS.DELETE')}</TooltipContent>
            </Tooltip>
          )}
        </div>

        {/* Header */}
        <div className={cn('flex items-center gap-3', !isExpanded && 'mb-2')}>
          <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-muted text-foreground">
            <IconComponent className="size-4.5" />
          </span>
          <p className="min-w-0 flex-1 truncate text-sm font-medium">{node.title}</p>
        </div>

        {/* Subtitle - Only show when not expanded, uses live config */}
        {!isExpanded && (
          <p className="text-xs leading-snug text-muted-foreground">{getSubtitleFromConfig()}</p>
        )}

        {/* Expandable Config Section */}
        {isExpanded && (
          <div className="mt-3 max-h-[400px] overflow-y-auto">
            {node.type === 'trigger' && renderTriggerConfig()}
            {node.type === 'condition' && renderConditionConfig()}
            {node.type === 'delay' && renderDelayConfig()}
            {node.type === 'action' && renderActionConfig()}

            <Button variant="outline" size="sm" className="mt-3 w-full" onClick={handleSaveConfig}>
              <Check className="size-3.5" />
              {t('COMMON.DONE')}
            </Button>
          </div>
        )}

        {/* Connection points — physical left/right, canvas space. The 40px
            wrapper is the hit area; the visible dot sits in its middle. */}
        <div
          onMouseUp={(e) => {
            e.stopPropagation();
            if (isConnecting && onPortDrop) onPortDrop(node.id, 'input');
          }}
          className={cn(
            'absolute top-1/2 left-[-20px] z-[3] flex size-10 -translate-y-1/2 items-center justify-center',
            isConnecting ? 'cursor-pointer' : 'cursor-default',
          )}
        >
          <span
            className={cn(
              'size-3 rounded-full border-2 transition-all',
              isConnecting
                ? 'border-foreground bg-foreground hover:scale-150 hover:shadow-[0_0_0_6px_var(--accent)]'
                : 'border-border bg-muted',
            )}
          />
        </div>
        <div
          onMouseDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
            if (onPortDragStart) onPortDragStart(node.id, 'output');
          }}
          className="absolute top-1/2 right-[-20px] z-[3] flex size-10 -translate-y-1/2 cursor-crosshair items-center justify-center"
        >
          <span className="size-3 rounded-full border-2 border-border bg-muted transition-all hover:scale-125 hover:border-foreground hover:bg-accent" />
        </div>
      </div>

      {/* Email Preview / Editor Dialog */}
      {showPreview && (
        <Dialog open={showPreview} onOpenChange={(open) => !open && setShowPreview(false)}>
          <DialogContent
            showCloseButton={false}
            className={cn(
              'flex max-h-[85vh] flex-col gap-0 p-0',
              localConfig.useAI === false ? 'sm:max-w-4xl' : 'sm:max-w-2xl',
            )}
          >
            <div className="flex items-center justify-between border-b px-4 py-3">
              <DialogTitle className="text-sm">
                {localConfig.useAI === false ? t('AUTOMATION.EDIT_TEMPLATE') : t('AUTOMATION.PREVIEW_EMAIL')}
              </DialogTitle>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => setShowPreview(false)}
                aria-label={t('COMMON.CANCEL')}
              >
                <X className="size-4" />
              </Button>
            </div>

            <div className="flex min-h-0 flex-1 overflow-hidden">
              {/* Left: edit fields (only when AI is off) */}
              {localConfig.useAI === false && (
                <div className="flex w-80 shrink-0 flex-col gap-4 overflow-auto border-e bg-background p-5">
                  <ConfigField label={`${t('AUTOMATION.SUBJECT')} *`}>
                    <Input
                      placeholder={t('AUTOMATION.SUBJECT_REQUIRED')}
                      value={localConfig.subject || ''}
                      onChange={(e) => handleConfigFieldChange('subject', e.target.value)}
                      aria-invalid={!localConfig.subject?.trim() || undefined}
                    />
                  </ConfigField>
                  <div className="border-b" />
                  <ConfigField label={t('AUTOMATION.HEADER_TITLE')}>
                    <Input
                      placeholder={t('AUTOMATION.HEADER_TITLE_PLACEHOLDER')}
                      value={localConfig.headerTitle || ''}
                      onChange={(e) => handleConfigFieldChange('headerTitle', e.target.value)}
                    />
                  </ConfigField>
                  <ConfigField label={t('AUTOMATION.HEADER_SUBTITLE')}>
                    <Input
                      placeholder="Subtitle..."
                      value={localConfig.headerSubtitle || ''}
                      onChange={(e) => handleConfigFieldChange('headerSubtitle', e.target.value)}
                    />
                  </ConfigField>
                  <ConfigField label={t('AUTOMATION.WELCOME_MSG')}>
                    <Textarea
                      rows={4}
                      placeholder={t('AUTOMATION.WELCOME_MSG_PLACEHOLDER')}
                      value={localConfig.welcomeMessage || ''}
                      onChange={(e) => handleConfigFieldChange('welcomeMessage', e.target.value)}
                    />
                  </ConfigField>
                  <ConfigField label={t('AUTOMATION.PRODUCT_SECTION_TITLE')}>
                    <Input
                      placeholder="Featured Products"
                      value={localConfig.productSectionTitle || ''}
                      onChange={(e) => handleConfigFieldChange('productSectionTitle', e.target.value)}
                    />
                  </ConfigField>
                  <ConfigField label={t('AUTOMATION.CTA_BUTTON')}>
                    <Input
                      placeholder="View Product →"
                      value={localConfig.ctaButtonText || ''}
                      onChange={(e) => handleConfigFieldChange('ctaButtonText', e.target.value)}
                    />
                  </ConfigField>
                  <ConfigField label={t('AUTOMATION.FOOTER')}>
                    <Input
                      placeholder={t('AUTOMATION.FOOTER_PLACEHOLDER')}
                      value={localConfig.footerText || ''}
                      onChange={(e) => handleConfigFieldChange('footerText', e.target.value)}
                    />
                  </ConfigField>
                </div>
              )}

              {/* Right: live preview */}
              <div className="flex-1 bg-muted">
                <iframe
                  srcDoc={localConfig.templateHtml
                    ? buildPreviewFromTemplate(localConfig.templateHtml, localConfig)
                    : buildEmailPreviewHtml({
                        headerTitle: localConfig.useAI !== false ? undefined : localConfig.headerTitle,
                        headerSubtitle: localConfig.useAI !== false ? undefined : localConfig.headerSubtitle,
                        welcomeMessage: localConfig.useAI !== false ? undefined : localConfig.welcomeMessage,
                        footerText: localConfig.useAI !== false ? undefined : localConfig.footerText,
                        productSectionTitle: localConfig.productSectionTitle,
                        ctaButtonText: localConfig.ctaButtonText,
                        productLayout: localConfig.productLayout || 'grid-cards',
                        isAI: localConfig.useAI !== false,
                      })
                  }
                  className="block h-[70vh] w-full border-0"
                  title="Email Preview"
                />
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};
